// Friend links — https://app.seabearing.com/?friend=<token>. A signed-in user makes one (see
// ./friends) and sends it however they like; whoever opens it is asked "Become friends with <name>?".
//
// This module is the URL side only, and deliberately tiny (no Supabase import): main.ts runs
// captureFriendLink() BEFORE the login wall, so the link survives it. The token is lifted out of the
// URL into localStorage and the address bar is cleaned (a reload doesn't re-trigger it and the token
// doesn't linger in history). It has to be localStorage, not the URL: a magic-link / OAuth sign-in
// redirects back to origin + path with the query dropped (auth/store redirectTo). After sign-in the
// app reads the stash, asks, and clears it once the question is settled.

const FRIEND_PARAM = 'friend';
const PENDING_KEY = 'seabearing:friend:pending';
// A stashed link older than this is dropped unasked (links themselves expire after 7 days; the server
// is the authority on validity — this only bounds how long an unanswered stash can sit).
const PENDING_TTL_MS = 30 * 24 * 3600 * 1000;
// The public app address the link points at. Not location.origin: the native shell and dev servers
// have origins nobody else can open.
const APP_URL = (import.meta.env?.VITE_APP_URL as string | undefined) ?? 'https://app.seabearing.com/';
// Server tokens are 32 base64url chars; accept a looser shape so a mangled link still reaches the
// server and gets a plain "isn't valid" instead of silently vanishing.
const TOKEN_RE = /^[A-Za-z0-9_-]{8,128}$/;

/** The shareable link for a token. */
export function friendLinkUrl(token: string): string {
  const u = new URL(APP_URL);
  u.searchParams.set(FRIEND_PARAM, token);
  return u.toString();
}

/** Pull a friend token out of a scanned/pasted friend link, or null if it isn't one. */
export function parseFriendLink(text: string): string | null {
  const t = (text || '').trim();
  if (!t) return null;
  try {
    const tok = new URL(t).searchParams.get(FRIEND_PARAM)?.trim();
    return tok && TOKEN_RE.test(tok) ? tok : null;
  } catch {
    return null; // not a URL
  }
}

/** If this page was opened from a friend link, stash its token and drop it from the address bar.
 *  Safe to call on every load; never throws. */
export function captureFriendLink(): void {
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(FRIEND_PARAM);
    if (raw == null) return;
    const token = raw.trim().slice(0, 256);
    if (token) stashFriendToken(token);
    url.searchParams.delete(FRIEND_PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch {
    /* no history API / storage — the link just isn't remembered across the login wall */
  }
}

/** Remember a friend token until the user has answered it (also used by the in-app QR scanner). */
export function stashFriendToken(token: string): void {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ token, at: Date.now() })); } catch { /* private mode */ }
}

/** The friend token still waiting for an answer, if any. */
export function pendingFriendToken(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { token?: unknown; at?: unknown };
    if (typeof p?.token !== 'string' || !p.token || typeof p.at !== 'number' || Date.now() - p.at > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return p.token;
  } catch {
    return null;
  }
}

/** Forget the stashed token (only if it is still `token`, when given — a newer link wins). */
export function clearPendingFriendToken(token?: string): void {
  try {
    if (token && pendingFriendToken() !== token) return;
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
